class ProductShippingCalculator extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.form = this.querySelector('[data-shipping-form]');
    this.input = this.querySelector('[data-shipping-zip]');
    this.submitButton = this.querySelector('[data-shipping-submit]');
    this.results = this.querySelector('[data-shipping-results]');

    if (!this.form || !this.input || !this.submitButton || !this.results) return;

    this.initialized = true;
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.form.addEventListener('submit', this.handleSubmit);
    this.input.addEventListener('input', this.handleInput);
  }

  disconnectedCallback() {
    if (!this.initialized) return;

    this.form.removeEventListener('submit', this.handleSubmit);
    this.input.removeEventListener('input', this.handleInput);
    this.abortController?.abort();
    this.initialized = false;
  }

  get rootUrl() {
    const root = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  get currentVariantId() {
    const productForm = document.getElementById(this.dataset.productFormId);
    const variantInput = productForm?.querySelector('[name="id"]');
    return Number(variantInput?.value || this.dataset.variantId);
  }

  handleInput() {
    const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
    this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    this.input.setCustomValidity('');
  }

  async handleSubmit(event) {
    event.preventDefault();

    const zip = this.input.value.replace(/\D/g, '');
    if (zip.length !== 8) {
      this.input.setCustomValidity(this.dataset.invalidZipMessage);
      this.input.reportValidity();
      return;
    }

    this.input.setCustomValidity('');
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setLoading(true);
    this.results.replaceChildren();

    let temporaryLineKey = null;

    try {
      const address = await this.fetchAddress(zip);
      const cartContext = await this.ensureProductInCart();
      const cart = cartContext.cart;
      temporaryLineKey = cartContext.temporaryLineKey;
      const rates = await this.fetchRates(zip, address);
      this.renderRates(rates, cart.currency || 'BRL');
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.renderMessage(error.message || this.dataset.errorMessage, 'error');
      }
    } finally {
      if (temporaryLineKey) {
        try {
          await this.removeTemporaryLine(temporaryLineKey);
        } catch {
          this.renderMessage(this.dataset.cleanupErrorMessage, 'error');
        }
      }

      this.setLoading(false);
    }
  }

  async fetchAddress(zip) {
    const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`, {
      headers: { Accept: 'application/json' },
      signal: this.abortController.signal
    });
    const data = await this.parseJson(response);

    if (!response.ok || data.erro || !data.uf) {
      throw new Error(this.dataset.addressErrorMessage);
    }

    return {
      city: data.localidade,
      province: data.estado || data.uf
    };
  }

  async ensureProductInCart() {
    const cart = await this.fetchJson(`${this.rootUrl}cart.js`);
    const variantId = this.currentVariantId;
    const productIsInCart = cart.items?.some((item) => Number(item.variant_id) === variantId);

    if (productIsInCart) {
      return { cart, temporaryLineKey: null };
    }

    const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const added = await this.fetchJson(`${this.rootUrl}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: { _shipping_calculator: token }
          }
        ]
      })
    });
    const temporaryLine = added.items?.[0] || added;

    if (!temporaryLine.key) {
      throw new Error(this.dataset.errorMessage);
    }

    return {
      cart: await this.fetchJson(`${this.rootUrl}cart.js`),
      temporaryLineKey: temporaryLine.key
    };
  }

  async removeTemporaryLine(lineKey) {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const response = await fetch(`${this.rootUrl}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });

      if (response.ok) return;
      if (attempt === 0) await this.wait(250);
    }

    throw new Error(this.dataset.cleanupErrorMessage);
  }

  async fetchRates(zip, address) {
    const parameters = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': address.province,
      'shipping_address[city]': address.city
    });
    const prepareUrl = `${this.rootUrl}cart/prepare_shipping_rates.json?${parameters}`;
    const asyncUrl = `${this.rootUrl}cart/async_shipping_rates.json?${parameters}`;

    const prepareResponse = await fetch(prepareUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: this.abortController.signal
    });

    if (!prepareResponse.ok) {
      return this.fetchDirectRates(parameters);
    }

    for (let attempt = 0; attempt < 12; attempt += 1) {
      if (attempt > 0) await this.wait(500);

      const response = await fetch(asyncUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal
      });

      if (!response.ok) break;

      const data = await response.json();
      if (Array.isArray(data?.shipping_rates)) return data.shipping_rates;
    }

    return this.fetchDirectRates(parameters);
  }

  async fetchDirectRates(parameters) {
    const response = await fetch(`${this.rootUrl}cart/shipping_rates.json?${parameters}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: this.abortController.signal
    });
    const data = await this.parseJson(response);

    if (!response.ok) {
      throw new Error(this.extractError(data));
    }

    return data.shipping_rates || [];
  }

  async fetchJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...options.headers
      },
      signal: this.abortController.signal
    });
    const data = await this.parseJson(response);

    if (!response.ok) {
      throw new Error(this.extractError(data));
    }

    return data;
  }

  async parseJson(response) {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  extractError(data) {
    if (data.description) return data.description;
    if (data.message) return data.message;

    const details = Object.values(data)
      .flat()
      .filter(Boolean)
      .join(' ');

    return details || this.dataset.errorMessage;
  }

  renderRates(rates, currency) {
    if (!rates.length) {
      this.renderMessage(this.dataset.noRatesMessage, 'error');
      return;
    }

    const wrapper = document.createElement('div');
    const heading = document.createElement('p');
    const list = document.createElement('ul');

    wrapper.className = 'product-shipping-calculator__rates';
    heading.className = 'product-shipping-calculator__rates-heading';
    heading.textContent = rates.length === 1 ? this.dataset.oneResultMessage : this.dataset.multipleResultsMessage;
    list.className = 'product-shipping-calculator__rates-list';
    list.setAttribute('role', 'list');

    rates.forEach((rate) => {
      const item = document.createElement('li');
      const name = document.createElement('span');
      const price = document.createElement('span');

      item.className = 'product-shipping-calculator__rate';
      name.textContent = rate.presentment_name || rate.name;
      price.className = 'product-shipping-calculator__rate-price';
      price.textContent = Number(rate.price) === 0 ? this.dataset.freeLabel : this.formatMoney(rate.price, rate.currency || currency);

      item.append(name, price);
      list.append(item);
    });

    wrapper.append(heading, list);
    this.results.replaceChildren(wrapper);
  }

  renderMessage(message, type) {
    const element = document.createElement('p');
    element.className = `product-shipping-calculator__message product-shipping-calculator__message--${type}`;
    element.textContent = message;
    this.results.replaceChildren(element);
  }

  formatMoney(value, currency) {
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return value;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
        style: 'currency',
        currency: currency || 'BRL'
      }).format(amount);
    } catch {
      return `${currency || 'BRL'} ${amount.toFixed(2)}`;
    }
  }

  setLoading(isLoading) {
    this.submitButton.disabled = isLoading;
    this.submitButton.classList.toggle('btn--loading', isLoading);
    this.form.setAttribute('aria-busy', String(isLoading));
  }

  wait(duration) {
    return new Promise((resolve) => {
      window.setTimeout(resolve, duration);
    });
  }
}

if (!customElements.get('product-shipping-calculator')) {
  customElements.define('product-shipping-calculator', ProductShippingCalculator);
}
